import type { ProColumns } from '@ant-design/pro-components';
import { numFormat } from '../format';
import { FIELD_METADATA, ColumnDisplayType } from './config';

/**
 * 列输入类型：支持配置表字段名或自定义列
 */
export type ColumnInput<T = any> =
  | keyof typeof FIELD_METADATA
  | { custom: ProColumns<T> };

/**
 * 根据类型格式化数值和单位
 */
const formatColumnValue = (value: any, type: ColumnDisplayType): string => {
  if (value === undefined || value === null || value === '') {
    const fallbacks = { currency: '0.00', percent: '0.00%', int: '0', ms: '0', text: '-' };
    return fallbacks[type] || '-';
  }
  const numVal = Number(value);
  switch (type) {
    case 'currency':
      return numFormat(numVal);
    case 'percent':
      return `${numFormat(numVal)}%`;
    case 'int':
      return numFormat(numVal, 0);
    case 'ms':
      return numFormat(numVal, 2);
    default:
      return String(value);
  }
};

/**
 * 获取 ProTable 列定义
 * @param dataIndex 字段名 (必须在配置表中存在)
 * @param overrides 可选：覆盖默认配置 (如修改 width, title 或 render)
 */
export const getColumnProps = <T = any>(
    dataIndex: keyof typeof FIELD_METADATA,
    overrides?: Partial<ProColumns<T>>
): ProColumns<T> => {
  const config = FIELD_METADATA[dataIndex as string];
  if (!config) {
    return { dataIndex, ...overrides } as ProColumns<T>;
  }
  
  const { title, type, width, renderText, render, ...restConfig } = config;
  
  return {
    title,
    dataIndex,
    width,
    renderText: renderText || ((text: any) => formatColumnValue(text, type)),
    render,
    ...restConfig,
    ...overrides,
  };
};

/**
 * 批量生成 ProTable columns (支持占位符)
 * @param keys 字段名数组或混合数组，支持插入自定义列
 * @param overrides 可选：针对特定字段的覆盖配置，key为字段名，value为覆盖配置
 * @example
 * // 基础用法（向后兼容）
 * getColumns(['adSpend', 'mediaRevenue', 'clicks'], {
 *   adSpend: { title: '收入(USD)' },
 * })
 *
 * @example
 * // 插入自定义列
 * getColumns([
 *   'clicks',
 *   'ctr',
 *   { custom: { title: '操作', dataIndex: 'action', render: (_, record) => <Button>编辑</Button> } },
 *   'adSpend'
 * ])
 */
export const getColumns = <T = any>(
    keys: Array<ColumnInput<T>>,
    overrides?: Record<string, Partial<ProColumns<T>>>
): ProColumns<T>[] => {
  return keys.map(key => {
    // 优先判断是否为字符串（配置表字段）
    if (typeof key === 'string') {
      return getColumnProps<T>(key as keyof typeof FIELD_METADATA, overrides?.[key]);
    }
    // 否则视为自定义列对象，直接返回 custom 属性
    return (key as { custom: ProColumns<T> }).custom;
  });
};

/**
 * 辅助函数：创建自定义列占位符
 * @param column 自定义列配置
 * @example
 * getColumns([
 *   'clicks',
 *   customColumn({ title: '操作', dataIndex: 'action', render: ... }),
 *   'ctr'
 * ])
 */
export const customColumn = <T = any>(column: ProColumns<T>): { custom: ProColumns<T> } => {
  return { custom: column };
};
